import { EmbeddedMessageMedium, MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { WorkflowType } from 'modules/domains';
import { SearchCombo } from 'modules/domains/segmentation';
import { TemplateId } from 'modules/domains/template';
import { ExperimentState } from 'modules/graphql';
import { Revision } from 'modules/workflows/components/JourneyRevisionHistory/types';
import { Nullable } from 'utils/tsHelpers';

import { RecurrenceSchedule } from './RecurrenceSchedule';

export type CampaignId = number;

export type AllMessageMedium = MessageMedium | WhatsAppMedium | EmbeddedMessageMedium;

/** TODO: move this to `src/constants` */
export enum CampaignType {
  ApiTriggered = 'ApiTriggered',
  Blast = 'Blast',
  Eligibility = 'Eligibility',
  WorkflowTriggered = 'WorkflowTriggered',

  /** NOTE: These doesn't represent a campaign in our database, they're just used for filtering campaigns */
  WithAI = 'WithAI',
  WithExperiment = 'WithExperiment',
}

export enum CampaignTypeFilter {
  Blast = 'Blast',
  Triggered = 'Triggered',
  FromWorkflow = 'FromWorkflow',
  WithAI = 'WithAI',
  WithExperiment = 'WithExperiment',
  Eligibility = 'Eligibility',
}

export enum ScheduleType {
  ProjectTimeZone = 'ProjectTimeZone',
  RecipientTimeZone = 'RecipientTimeZone',
}

/** TODO: move this to `src/constants` */
export enum CampaignState {
  Aborted = 'Aborted',
  Archived = 'Archived',
  Draft = 'Draft',
  Finished = 'Finished',
  Ready = 'Ready',
  Recurring = 'Recurring',
  Running = 'Running',
  Recalling = 'Recalling',
  Recalled = 'Recalled',
  Scheduled = 'Scheduled',
}

/**
 * @deprecated
 * Updated code should pull `MessageMedium` from `constants/messageMediums`
 */
export { MessageMedium } from 'constants/messageMediums';
export * from './folder';
export * from './RecurrenceSchedule';

export type QuietHours = {
  startTime: string;
  endTime: string;
  sendSkip: boolean;
} | null;

export type CampaignRateLimit = {
  rateLimitPerMin: number;
  useRateLimit: boolean;
} | null;

/** TODO: move this to `src/constants` */
export enum DataScienceOptimization {
  ChannelOptimization = 'ChannelOptimization',
  FrequencyOptimization = 'FrequencyOptimization',
  SendTimeOptimization = 'SendTimeOptimization',
}

/** TODO: move this to `src/constants` */
export enum ExperimentType {
  EmailBody = 'EmailBody',
  FrequencyOptimization = 'FrequencyOptimization',
  FromNameAndSender = 'FromNameAndSender',
  PreheaderText = 'PreheaderText',
  SendTime = 'SendTime',
  SendTimeOptimization = 'SendTimeOptimization',
  SubjectLine = 'SubjectLine',
  WholeEmail = 'WholeEmail',
}

export enum AiCreation {
  NextBestAction = 'NextBestAction',
}

export enum WinningCriteriaType {
  ConversionEvent = 'ConversionEvent',
  SystemEvent = 'SystemEvent',
}

export type ExperimentVariant = { id: number; sendTime?: Nullable<string> };

export type Experiment = {
  experimentType: ExperimentType;
  id: number;
  state: ExperimentState;
  createdAt: string;
  variations: ExperimentVariant[];
  name: string;
  winningCriteria: {
    type: WinningCriteriaType;
  };
};

export type CampaignLabel = {
  id: number;
  name: string;
  projectId?: number;
};

export type AudienceList = {
  id: number;
  name: string;
};

/** A day of the week */
export enum Day {
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
  Sunday = 'sunday',
}

export type SkipDaysOfWeek = Record<Day, boolean>;

type GraphQLEndDate = {
  timestamp: string;
} | null;

/**
 * @see https://github.com/Iterable/iterable/blob/ad1422d02c1301327dff6d3ffc62f9faaceaefaa/platform/app/com/iterable/models/SchedulerMsgParams.scala#L33-L45
 */
export type BaseSchedulerMsgParams = {
  scheduleType: ScheduleType;
  defaultTimeZone: string | null;
  startTimeZone: string | null;
  schedulerMsgIds?: string[];
  recurDays: number | null;
  precreateHours: number | null;
  skipDaysOfWeek?: SkipDaysOfWeek;
  endDate: GraphQLEndDate;
  automaticSendTime?: boolean;
  windowHours: number;
  recurSchedule?: RecurrenceSchedule;
};

export type SchedulerMsgParams = BaseSchedulerMsgParams | null;

export type ConversionEvent = {
  name: string;
  attributionMode: { periodHours: number; name: string };
};

export type Conversion = {
  events: ConversionEvent[];
};

export type CustomConversion = {
  eventName: string;
  attributionMode: string;
  attributionPeriodHours?: number | null;
};

export type CustomConversions = CustomConversion[];

export type PriorityLevel = 100.5 | 200.5 | 300.5 | 400.5;

export type Journey = {
  id: number;
  name: string;
  workflowType: WorkflowType;
  revisions: {
    results: Revision[];
  };
  allNodes: {
    id: number;
    resource: {
      campaign?: { id: number };
    } | null;
  }[];
  goals: {
    isActive: boolean;
    conversions: {
      eventName: string;
      attributionMode: AttributionMode;
      attributionPeriodHours: number;
    }[];
  }[];
};

export type CampaignUser = {
  id: string;
  fullName: string;
  avatarUrl?: string;
};

// When does an embedded campaign display until?
export interface EmbeddedDisplayUntil {
  // The date in which the embedded campaign should stop displaying
  // As per convo with @eaminz, this is given in UTC which should then be
  // adjusted to the project timezone.
  endDate?: string;
  // The search query that will determine when the embedded campaign should stop
  // displaying (e.g. when a user matches the search query)
  searchQuery?: SearchCombo;
}

export type Campaign = {
  id: CampaignId;
  name: string;
  createdAt: string;
  createdBy?: string;
  creatorUser: CampaignUser;
  updatedAt: string;
  updatedBy: string;
  startAt: string | null;
  endedAt: string | null;

  priorityLevel: PriorityLevel;
  expectedSendSize?: number;

  templateId: TemplateId;
  templateContentPreview: string;

  messageMedium: MessageMedium;
  campaignState: CampaignState;
  campaignTypeDefinition: CampaignType;

  labels: CampaignLabel[];
  lists: AudienceList[];
  conversions: Conversion[];
  suppressionLists: AudienceList[];
  mobileAppIds: number[];

  experiments: Experiment[];
  customQuietHours: QuietHours;
  customRateLimit: CampaignRateLimit;
  ignoreFrequencyCap: boolean;
  dataScienceOptimizations: DataScienceOptimization[];

  schedulerMsgParams: SchedulerMsgParams;

  nextRunTime: string | null;

  journey?: Journey;

  embeddedPriorityOrder?: number;
  placementId?: number;
  displayUntil?: EmbeddedDisplayUntil;

  campaignAlertsEnabled?: boolean;
  isJourneyAssociated?: boolean;
  aiCreation: AiCreation | null;
};

/** TODO: move this to `src/constants` */
export enum RateLimitUnit {
  Minute = 'minute',
  Second = 'second',
}

export enum AttributionMode {
  AttributionPeriod = 'AttributionPeriod',
  LastMessaged = 'LastMessaged',
  ExplicitlySet = 'ExplicitlySet',
}
